export type DatasheetDownloadItem<T> = {
  file: T | null | undefined;
  enabled?: boolean | null;
};

export type ProductDatasheetPermissionData<T = string> = {
  datasheet1_file?: T | null;
  datasheet1_enabled?: boolean | null;
  datasheet2_file?: T | null;
  datasheet3_file?: T | null;
};

type ProductDatasheetPermissionLangData<T = string> =
  ProductDatasheetPermissionData<T> & {
    lang?: string | number | null;
  };

type ProductDatasheetPermissionRecord<T = string> =
  ProductDatasheetPermissionData<T> & {
    lang_data?: ProductDatasheetPermissionLangData<T>[];
  };

export type ResolvedDatasheetDownload<T> = {
  file: T | null;
  canDownload: boolean;
  requiresLogin: boolean;
};

export const PRODUCT_DATASHEET_PERMISSION_FIELDS = [
  "datasheet1_file",
  "datasheet1_enabled",
  "datasheet2_file",
  "datasheet3_file",
];

const chinesePathPattern = /^(?:\/)?(?:cn)(?:\/|$)/;

function getPathLang(pathname?: string | null) {
  return chinesePathPattern.test(pathname ?? "") ? 1 : 2;
}

export function resolveDatasheetDownload<T>(
  items: DatasheetDownloadItem<T>[],
  userLevel?: number | null
): ResolvedDatasheetDownload<T> {
  const hasUserLevel = Number.isInteger(userLevel) && Number(userLevel) > 0;

  if (hasUserLevel) {
    const item = items[Number(userLevel) - 1];
    const file = item?.file || null;

    return {
      file,
      canDownload: Boolean(file),
      requiresLogin: false,
    };
  }

  const datasheet1 = items[0];
  const item = datasheet1?.file
    ? datasheet1
    : items.find((datasheet) => Boolean(datasheet.file));
  const file = item?.file || null;

  if (!file) {
    return {
      file: null,
      canDownload: false,
      requiresLogin: false,
    };
  }

  const canDownload = item === datasheet1 && item.enabled === true;

  return {
    file,
    canDownload,
    requiresLogin: !canDownload,
  };
}

export function productToDatasheetDownloadItems<T>(
  product?: ProductDatasheetPermissionData<T> | null
): DatasheetDownloadItem<T>[] {
  return [
    {
      file: product?.datasheet1_file,
      enabled: product?.datasheet1_enabled,
    },
    { file: product?.datasheet2_file },
    { file: product?.datasheet3_file },
  ];
}

function appendPublishFilters(url: URL) {
  url.searchParams.append(
    "filter[_or][0][_and][0][publish][publish_id][status][_eq]",
    "PUBLISH"
  );
  url.searchParams.append(
    "filter[_or][0][_and][1][publish][publish_id][enable][_eq]",
    "false"
  );

  url.searchParams.append(
    "filter[_or][1][_and][0][publish][publish_id][status][_eq]",
    "PUBLISH"
  );
  url.searchParams.append(
    "filter[_or][1][_and][1][publish][publish_id][enable][_eq]",
    "true"
  );
  url.searchParams.append(
    "filter[_or][1][_and][2][publish][publish_id][publish_date][_lte]",
    "$NOW"
  );
}

function mergeDirectusDatasheetPermissionData(
  product: ProductDatasheetPermissionRecord<string>,
  pathname?: string | null
): ProductDatasheetPermissionData<string> {
  const currentLang = getPathLang(pathname);

  if (currentLang === 1) {
    return product;
  }

  const langData = product.lang_data?.find((item) => {
    return Number(item.lang) === currentLang;
  });

  if (!langData) {
    return product;
  }

  return PRODUCT_DATASHEET_PERMISSION_FIELDS.reduce<
    ProductDatasheetPermissionData<string>
  >(
    (merged, field) => {
      if (Object.prototype.hasOwnProperty.call(langData, field)) {
        return {
          ...merged,
          [field]: langData[field as keyof ProductDatasheetPermissionData],
        };
      }

      return merged;
    },
    { ...product }
  );
}

export function buildProductDatasheetQueryUrl({
  dataServer,
  productId,
  pathname,
}: {
  dataServer: string;
  productId: string | number;
  pathname?: string | null;
}) {
  const dataServerUrl = dataServer.replace(/\/$/, "");
  const url = new URL(`${dataServerUrl}/items/product`);
  const fields = [
    "id",
    "publish.publish_id.*",
    "lang_data.lang",
    ...PRODUCT_DATASHEET_PERMISSION_FIELDS.flatMap((field) => [
      field,
      `lang_data.${field}`,
    ]),
  ];

  url.searchParams.append("filter[lang_is_main][_eq]", "true");
  url.searchParams.append("fields", fields.join(","));
  appendPublishFilters(url);
  url.searchParams.append("filter[id][_eq]", productId.toString());
  url.searchParams.append("page", "1");
  url.searchParams.append("limit", "1");

  return url;
}

type DatasheetPermissionFetchResponse<T> = {
  data?:
    | ProductDatasheetPermissionRecord<T>
    | ProductDatasheetPermissionRecord<T>[]
    | null;
};

type DatasheetPermissionFetcher = (
  url: string,
  init?: RequestInit
) => Promise<{
  ok: boolean;
  json: () => Promise<DatasheetPermissionFetchResponse<string>>;
}>;

export async function fetchLatestDatasheetDownload({
  dataServer = process.env.NEXT_PUBLIC_DATASERVER,
  productId,
  pathname,
  userLevel,
  fetcher = fetch,
}: {
  dataServer?: string | null;
  productId?: string | number | null;
  pathname?: string | null;
  userLevel?: number | null;
  fetcher?: DatasheetPermissionFetcher;
}): Promise<ResolvedDatasheetDownload<string> | null> {
  if (!dataServer || !productId) {
    return null;
  }

  const res = await fetcher(
    buildProductDatasheetQueryUrl({
      dataServer,
      productId,
      pathname,
    }).toString(),
    { cache: "no-store" }
  ).catch(() => null);

  if (!res) {
    return null;
  }

  if (!res.ok) {
    return null;
  }

  const json = await res.json();
  const product = Array.isArray(json.data) ? json.data[0] : json.data;

  if (!product) {
    return null;
  }

  return resolveDatasheetDownload(
    productToDatasheetDownloadItems(
      mergeDirectusDatasheetPermissionData(product, pathname)
    ),
    userLevel
  );
}
